import _ from "lodash"
import { useEffect, useRef, useState } from "react"
import type { Channel, DefaultGenerics, MessageFilters, MessageResponse } from "stream-chat"

import { useUser } from "@modules/auth/hooks/useUser"
import { useChatClient } from "@modules/chat/hooks/useChatClient"
import { DEFAULT_PAGINATION_LIMIT } from "@modules/chat/utils/chatConstants"
import { useServicesContext } from "@modules/core/services/services.context"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { useQueryClient } from "@tanstack/react-query"

export type SearchResultType =
  | { entity: "message"; content: MessageResponse<DefaultGenerics> }
  | { entity: "channel"; content: Channel<DefaultGenerics> }

export const usePaginatedChatSearch = (searchTerms: string = "") => {
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<Error | boolean>(false)
  const [searchResults, setSearchResults] = useState<SearchResultType[]>()

  const user = useUser()
  const offset = useRef(0)
  const hasMoreResults = useRef(true)
  const queryInProgress = useRef(false)
  const { chatClient } = useChatClient()
  const { exploreApi } = useServicesContext()
  const queryClient = useQueryClient()

  const done = () => {
    queryInProgress.current = false
    setLoading(false)
    setRefreshing(false)
  }

  const reset = () => {
    setSearchResults(undefined)
    offset.current = 0
    hasMoreResults.current = true
  }

  const fetchMessages = async () => {
    if (!searchTerms) {
      reset()
      done()
      return
    }

    if (queryInProgress.current) {
      done()
      return
    }

    setLoading(true)

    try {
      queryInProgress.current = true

      if (!hasMoreResults.current) {
        queryInProgress.current = false
        done()
        return
      }

      const userId = user?.id ?? ""

      const dataUsers = await queryClient.fetchQuery({
        queryKey: ["exploreSearch", searchTerms, ExploreFilter.PROFILE],
        queryFn: async () => {
          return exploreApi.searchExplore(searchTerms, {
            filter: ExploreFilter.PROFILE,
            size: 40,
          })
        },
      })

      const dataChannels = await chatClient?.queryChannels(
        dataUsers.content.length > 0
          ? {
              $or: [
                { $and: [{ name: { $autocomplete: searchTerms } }, { members: { $in: [userId] } }] },
                {
                  $and: [
                    { created_by_id: { $in: dataUsers.content.map((u) => u.content.id) } },
                    { members: { $in: [userId] } },
                  ],
                },
                {
                  $and: [
                    { members: { $in: dataUsers.content.map((u) => u.content.id) } },
                    { members: { $in: [userId] } },
                    { type: { $eq: "messaging" } },
                  ],
                },
              ],
            }
          : {
              name: { $autocomplete: searchTerms },
              members: { $in: [userId] },
            },
        { last_message_at: -1 },
        { limit: 5 },
      )

      let messageFilters: MessageFilters<DefaultGenerics> = { text: { $q: searchTerms } }

      if (dataUsers && dataUsers.content.length > 0) {
        messageFilters = {
          $or: [{ text: { $q: searchTerms } }, { "user.id": { $in: dataUsers.content.map((u) => u.content.id) } }],
        }
      }

      const dataMessages = await chatClient?.search(
        {
          members: {
            $in: [chatClient.user?.id || null],
          },
        },
        messageFilters,
        {
          limit: DEFAULT_PAGINATION_LIMIT,
          offset: offset.current,
        },
      )

      const newMessages = dataMessages?.results.map((r) => r.message)

      if (!newMessages) {
        queryInProgress.current = false
        done()
        return
      }

      if (offset.current === 0) {
        setSearchResults(
          (dataChannels?.map((c) => ({ entity: "channel", content: c })) as SearchResultType[]).concat(
            newMessages.map((c) => ({ entity: "message", content: c })) as SearchResultType[],
          ),
        )
      } else {
        setSearchResults(
          _.uniqBy(
            _.concat(
              searchResults ?? [],
              newMessages.map((c) => ({ entity: "message", content: c })),
            ),
            (it) => it.content.id,
          ),
        )
      }
      offset.current = offset.current + newMessages.length

      if (newMessages.length < DEFAULT_PAGINATION_LIMIT) {
        hasMoreResults.current = false
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err)
      } else {
        setError(true)
      }
    }

    done()
  }

  const loadMore = async () => {
    setLoadingMore(true)
    await fetchMessages()
    setLoadingMore(false)
  }

  const refreshList = () => {
    if (!chatClient?.user?.id) {
      return
    }

    offset.current = 0
    hasMoreResults.current = true

    setRefreshing(true)
    fetchMessages()
  }

  const reloadList = () => {
    reset()
    setSearchResults([])
    fetchMessages()
  }

  useEffect(() => {
    reloadList()
  }, [searchTerms])

  return {
    error,
    loading,
    loadMore,
    loadingMore,
    searchResults,
    refreshing,
    refreshList,
    reloadList,
    reset,
  }
}
